import { Core } from "@faire/design-tokens";
import { SearchableSelect } from "@faire/help-center-utils/components/SearchableSelect/SearchableSelect";
import {
  getBrandBusinessName,
  getBrandToken,
} from "@faire/help-center-utils/serialized-data/getBrand";
import {
  getRetailerBusinessName,
  getRetailerToken,
} from "@faire/help-center-utils/serialized-data/getRetailer";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Modal } from "@faire/web--source/slate/Modal";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useState } from "@faire/web--source/ui/hooks/useState";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import logout from "@faire/web-api--source/endpoints/www/api/user/logout/post";
import { trackHelpCenterActionModalLogoutClick } from "@faire/web-api--source/events/helpCenter/click/actionModalLogout";
import { trackHelpCenterActionModalSubmit } from "@faire/web-api--source/events/helpCenter/submit/actionModal";
import home from "@faire/web-api--source/routes/www/root";
import brands from "@faire/web-api--source/routes/www/support/brands";
import retailers from "@faire/web-api--source/routes/www/support/retailers";
import React, { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { useActionTypes } from "../hooks/useActionTypes";
import { useBrandOrders } from "../hooks/useBrandOrders";
import { IModalContent, useModalContent } from "../hooks/useModalContent";
import { useShowMessage } from "../hooks/useShowMessage";

import { ButtonText } from "./ButtonText";
import { DropdownOption } from "./DropdownOption";
import { MessageInfo } from "./MessageInfo";
import { ActionModalContent, OptionName } from "./styles";
import { IDropdownOption } from "./types";

export const ActionModal: React.FC = () => {
  const [modalContent, setModalContent] = useState<IModalContent | undefined>(
    undefined
  );
  const title = modalContent?.title;
  const description = modalContent?.description;

  const [selectedOption, setSelectedOption] = useState<
    IDropdownOption | undefined
  >(undefined);
  const location = useLocation();
  const history = useHistory();

  const businessName = getRetailerBusinessName() ?? getBrandBusinessName();
  const { strictLocalize } = useStrictLocalization();
  const { actionType, clearActionType } = useActionTypes();
  const { isUpdateCommissionModal, dropdownOptions } = useBrandOrders();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const modalContentByActionId = useModalContent();
  const {
    showEmptyState,
    showNotShippedMessage,
    showBeingFulfilledMessage,
    showNeedExtensionMessage,
    redirectLink,
  } = useShowMessage(dropdownOptions, selectedOption);

  const canSubmit =
    (!!selectedOption &&
      !showNotShippedMessage &&
      !showBeingFulfilledMessage &&
      !showNeedExtensionMessage) ||
    showEmptyState;

  useEffect(() => {
    if (actionType) {
      const openModal = () => {
        if (!modalContent && actionType) {
          const modalContent = modalContentByActionId[actionType.id];
          setIsModalOpen(true);
          setModalContent(modalContent);
        }
      };
      openModal();
    }
  }, [actionType, modalContent, modalContentByActionId, strictLocalize]);

  const isLandingPage =
    location?.pathname === brands() || location?.pathname === retailers();

  const handleSubmit = () => {
    if (!showEmptyState) {
      trackHelpCenterActionModalSubmit(actionType?.label ?? "");
    }
    if (isLandingPage) {
      closeModal();
    }
  };

  const handleLogout = async () => {
    const retailerToken = getRetailerToken() ?? "";
    const brandToken = getBrandToken() ?? "";
    trackHelpCenterActionModalLogoutClick(retailerToken, brandToken);
    await logout();
    history.push(home());
  };

  const closeModal = () => {
    if (isModalOpen) {
      clearActionType();
      setIsModalOpen(false);
      setModalContent(undefined);
      setSelectedOption(undefined);
    }
  };

  return (
    <Modal
      contentStyle={{
        overflow: "visible",
      }}
      padding="0"
      isOpen={isModalOpen}
      onClose={closeModal}
      height="auto"
    >
      <ActionModalContent>
        <Typography variant="pageHeaderSerifRegular" align="center">
          {title}
        </Typography>
        <Spacer height="1x" />
        <Typography align="center">{description}</Typography>
        <Spacer height="3x" />
        <SearchableSelect
          onSelect={setSelectedOption}
          options={dropdownOptions}
          renderOption={(optionProps) => (
            <DropdownOption
              {...optionProps}
              selected={optionProps.option.value === selectedOption?.value}
            />
          )}
          placeholder={
            isUpdateCommissionModal
              ? strictLocalize({
                  defaultMessage: "Select retailer",
                  description: {
                    text: "Placeholder for a dropdown",
                  },
                })
              : strictLocalize({
                  defaultMessage: "Select order",
                  description: {
                    text: "Placeholder for a dropdown",
                  },
                })
          }
          searchKeys={["name", "customerBrandOrderToken"]}
        >
          {selectedOption?.name ? (
            <Row align="center">
              {selectedOption?.date ? (
                <Column flex={1}>
                  <Typography>{selectedOption.date}</Typography>
                </Column>
              ) : null}
              <OptionName>{selectedOption.name}</OptionName>
              {selectedOption?.customerBrandOrderToken ? (
                <>
                  <Spacer size={12} />
                  <Column flex={1}>
                    <Row align="center">
                      <Typography color={Core.text.subdued}>
                        {selectedOption.customerBrandOrderToken}
                      </Typography>
                      <Spacer size={26} />
                    </Row>
                  </Column>
                </>
              ) : null}
            </Row>
          ) : null}
        </SearchableSelect>
        <MessageInfo {...{ selectedOption, dropdownOptions }} />

        <Spacer height="5x" />
        <Button
          variant={showEmptyState ? "secondary" : "primary"}
          disabled={!canSubmit}
          onClick={handleSubmit}
          linkTo={redirectLink}
        >
          <ButtonText {...{ selectedOption, dropdownOptions, modalContent }} />
        </Button>
        <Spacer height="2x" />
        <Row justify="center" align="baseline">
          {businessName ? (
            <Typography color={Core.text.subdued}>
              <StrictLocalMsg
                defaultMessage="Signed in as <b>{businessCompanyName}</b>"
                values={{ businessCompanyName: businessName }}
              />
            </Typography>
          ) : null}
          <Spacer width="1x" />
          <Button
            variant="tertiary"
            onClick={handleLogout}
            style={{ minWidth: "60px" }}
          >
            <Typography color={Core.text.subdued}>
              <StrictLocalMsg
                defaultMessage="Sign Out"
                description={{ text: "Button to log out" }}
              />
            </Typography>
          </Button>
        </Row>
      </ActionModalContent>
    </Modal>
  );
};
